/**
 * Push Subscription Service (REQ-008)
 * Manages browser-side Web Push subscription lifecycle
 */

import { log } from "@/lib/log";

/**
 * The API root, `/api` included - the same value `services/api.ts` and
 * `services/api/apiClient.ts` use as their baseURL.
 *
 * This file used to hold the origin alone and add `/api` to each path, which is
 * true of the development default and false of `VITE_API_URL` in every built
 * environment: `.env.production` sets `https://travelhealthpass.app/api`, so
 * every request went to `/api/api/push/...` and answered 404. Nothing said so,
 * because the one caller catches everything and returns null, so the toggle in
 * settings simply did nothing.
 */
const API_BASE = (
  import.meta.env.VITE_API_URL ?? "http://localhost:8088/api"
).replace(/\/$/, "");

function urlBase64ToUint8Array(base64String: string): Uint8Array<ArrayBuffer> {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = atob(base64);
  const buffer = new ArrayBuffer(rawData.length);
  const output = new Uint8Array(buffer);
  for (let i = 0; i < rawData.length; i++) {
    output[i] = rawData.charCodeAt(i);
  }
  return output;
}

async function getVapidPublicKey(token: string): Promise<string> {
  const res = await fetch(`${API_BASE}/push/vapid-public-key`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) throw new Error("Failed to fetch VAPID public key");
  const json = (await res.json()) as { data: { publicKey: string } };
  return json.data.publicKey;
}

/**
 * The app's one service worker, already registered at load by registerSW.js.
 *
 * This used to register a second script, /sw.js, at the same scope - and a
 * scope holds exactly one registration, so the two fought. The push worker won
 * the install and then sat in "waiting" forever: it has no message handler, so
 * "Load new app version." posted SKIP_WAITING into a void and nothing happened,
 * and it has no fetch handler either, so had it ever activated the app would
 * have lost offline support and stopped being installable.
 *
 * Push and notification clicks are handled in service-worker.ts. There is
 * nothing this needs that lives anywhere else.
 */
async function registerServiceWorker(): Promise<ServiceWorkerRegistration | null> {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.ready;
  } catch {
    return null;
  }
}

/**
 * Subscribe the current browser to push notifications.
 * Returns the subscription ID from the backend, or null on failure.
 */
export async function subscribeToPush(token: string): Promise<string | null> {
  try {
    const reg = await registerServiceWorker();
    if (!reg) {
      log.warn("Push: no service worker registration, cannot subscribe");
      return null;
    }

    const vapidKey = await getVapidPublicKey(token);
    const subscription = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(vapidKey),
    });

    const sub = subscription.toJSON();
    const res = await fetch(`${API_BASE}/push/subscribe`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({
        endpoint: sub.endpoint,
        p256dh: sub.keys?.p256dh,
        auth: sub.keys?.auth,
        userAgent: navigator.userAgent,
      }),
    });

    if (!res.ok) {
      log.warn("Push: the backend refused the subscription", res.status);
      return null;
    }
    const json = (await res.json()) as { data: { id: string } };
    return json.data.id;
  } catch (error) {
    // Silence here is what hid a 404 on every production request for months:
    // the caller turns null into "nothing happened" and the toggle springs
    // back with no explanation. The failure is still not thrown - a refused
    // permission is a normal answer - but it is no longer invisible.
    log.warn("Push: subscription failed", error);
    return null;
  }
}

/**
 * Unsubscribe from push notifications
 */
export async function unsubscribeFromPush(
  token: string,
  subscriptionId: string,
): Promise<void> {
  // Unsubscribe from browser
  const reg = await navigator.serviceWorker?.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  await sub?.unsubscribe();

  // Remove from backend
  await fetch(`${API_BASE}/push/subscribe/${subscriptionId}`, {
    method: "DELETE",
    headers: { Authorization: `Bearer ${token}` },
  });
}

/**
 * Check if push is supported and permission is granted
 */
export function isPushSupported(): boolean {
  return "serviceWorker" in navigator && "PushManager" in window;
}

export async function getPushPermissionState(): Promise<NotificationPermission> {
  if (!isPushSupported()) return "denied";
  return Notification.permission;
}
